import React, { useId, useState } from "react";

type FoodLogPayload = {
  date: string;
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  meal: string;
};

type Props = {
  date: string;
  onSubmit: (payload: FoodLogPayload) => Promise<void> | void;
  placeholder?: string;
};

export const FoodLogEntry: React.FC<Props> = ({
  date,
  onSubmit,
  placeholder,
}) => {
  const baseId = useId();
  const nameId = `${baseId}-name`;
  const caloriesId = `${baseId}-calories`;
  const proteinId = `${baseId}-protein`;
  const carbsId = `${baseId}-carbs`;
  const fatId = `${baseId}-fat`;
  const mealId = `${baseId}-meal`;
  const [name, setName] = useState("");
  const [calories, setCalories] = useState<number>(0);
  const [protein, setProtein] = useState<number>(0);
  const [carbs, setCarbs] = useState<number>(0);
  const [fat, setFat] = useState<number>(0);
  const [meal, setMeal] = useState<string>("Breakfast");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const payload: FoodLogPayload = {
      date,
      name: name.trim(),
      calories: Number(calories) || 0,
      protein: Number(protein) || 0,
      carbs: Number(carbs) || 0,
      fat: Number(fat) || 0,
      meal,
    };
    await onSubmit(payload);

    setName("");
    setCalories(0);
    setProtein(0);
    setCarbs(0);
    setFat(0);
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={styles.form}
      aria-label="Log food entry"
    >
      <div style={styles.fieldGroup}>
        <label htmlFor={nameId} style={styles.label}>
          Food name
        </label>
        <input
          id={nameId}
          style={styles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={placeholder ?? "e.g., Oatmeal with berries"}
          required
        />
      </div>
      <div style={styles.grid}>
        <div style={styles.fieldGroup}>
          <label htmlFor={caloriesId} style={styles.label}>
            Calories
          </label>
          <input
            id={caloriesId}
            style={styles.input}
            type="number"
            value={calories}
            onChange={(e) => setCalories(Number(e.target.value))}
          />
        </div>
        <div style={styles.fieldGroup}>
          <label htmlFor={proteinId} style={styles.label}>
            Protein (g)
          </label>
          <input
            id={proteinId}
            style={styles.input}
            type="number"
            value={protein}
            onChange={(e) => setProtein(Number(e.target.value))}
          />
        </div>
        <div style={styles.fieldGroup}>
          <label htmlFor={carbsId} style={styles.label}>
            Carbs (g)
          </label>
          <input
            id={carbsId}
            style={styles.input}
            type="number"
            value={carbs}
            onChange={(e) => setCarbs(Number(e.target.value))}
          />
        </div>
        <div style={styles.fieldGroup}>
          <label htmlFor={fatId} style={styles.label}>
            Fat (g)
          </label>
          <input
            id={fatId}
            style={styles.input}
            type="number"
            value={fat}
            onChange={(e) => setFat(Number(e.target.value))}
          />
        </div>
      </div>
      <div style={styles.fieldGroup}>
        <label htmlFor={mealId} style={styles.label}>
          Meal
        </label>
        <select
          id={mealId}
          style={styles.input}
          value={meal}
          onChange={(e) => setMeal(e.target.value)}
        >
          <option>Breakfast</option>
          <option>Lunch</option>
          <option>Dinner</option>
          <option>Snacks</option>
        </select>
      </div>
      <button type="submit" style={styles.submit}>
        Add food
      </button>
    </form>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: 12,
    borderRadius: 8,
    background: "#252525",
  },
  fieldGroup: { display: "flex", flexDirection: "column" as const, gap: 6 },
  label: { fontSize: 12, color: "#ddd" },
  input: {
    padding: "12px",
    borderRadius: 6,
    border: "1px solid #444",
    background: "#333",
    color: "#fff",
    minWidth: 0,
  },
  grid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 },
  submit: {
    marginTop: 6,
    padding: "12px",
    borderRadius: 6,
    border: "none",
    background: "#ff6b35",
    color: "#fff",
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default FoodLogEntry;
